import { useEffect, useState, type FormEvent } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Smartphone } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import logoFull from '../assets/images/logo.svg';
import { buildApiUrl } from '../config/api';
import { AUTH0_EMAIL_CONNECTION, AUTH0_SMS_CONNECTION } from '../config/auth0Connections';
import { TenantAppearanceDock } from '../components/tenant/TenantAppearanceDock';
import {
  LoginInternationalPath,
  type InternationalStep,
} from '../components/tenant/LoginInternationalPath';
import { formatPhoneInputLive, isUnitedStatesPhone, loginPhoneKind, phoneCopyValue } from '../utils/phone-display';

const fieldClass =
  'w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-4 text-base text-gray-900 outline-none focus:border-red-300 focus:bg-white dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10';

function emailLooksValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function LoginPage() {
  const { loginWithRedirect } = useAuth0();
  const [searchParams] = useSearchParams();
  const isProspect = searchParams.get('intent') === 'prospect';
  const groupInviteToken = searchParams.get('group-invite');
  const [inviterName, setInviterName] = useState<string | null>(null);
  const [inviteLookupDone, setInviteLookupDone] = useState(!groupInviteToken);
  const [internationalStep, setInternationalStep] = useState<InternationalStep | null>(null);
  const [phoneStep, setPhoneStep] = useState(false);
  const [emailStep, setEmailStep] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneNotice, setPhoneNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!groupInviteToken) return;
    let cancelled = false;
    setInviteLookupDone(false);
    fetch(buildApiUrl(`/public/prospect-group-invite?token=${encodeURIComponent(groupInviteToken)}`))
      .then(async (response) => {
        if (!response.ok) return null;
        const payload = (await response.json()) as { data?: { inviterName?: string } };
        return payload.data?.inviterName?.trim() || null;
      })
      .then((name) => {
        if (!cancelled) setInviterName(name);
      })
      .catch(() => {
        if (!cancelled) setInviterName(null);
      })
      .finally(() => {
        if (!cancelled) setInviteLookupDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [groupInviteToken]);

  const returnTo = isProspect ? '/my-information' : '/';

  const openPhoneStep = () => {
    setInternationalStep(null);
    setEmailStep(false);
    setPhoneError(null);
    setPhoneNotice(null);
    setPhoneStep(true);
  };

  const openEmailStep = () => {
    setInternationalStep(null);
    setPhoneStep(false);
    setPhoneNotice(null);
    setEmailError(null);
    setEmailStep(true);
  };

  const startSmsLogin = () => {
    const hint = phoneCopyValue(phone);
    loginWithRedirect({
      authorizationParams: {
        connection: AUTH0_SMS_CONNECTION,
        prompt: 'login',
        ...(hint ? { login_hint: hint } : {}),
      },
      appState: { returnTo },
    });
  };

  const startEmailLogin = (address: string) => {
    const hint = address.trim();
    loginWithRedirect({
      authorizationParams: {
        connection: AUTH0_EMAIL_CONNECTION,
        prompt: 'login',
        ...(hint ? { login_hint: hint } : {}),
      },
      appState: { returnTo },
    });
  };

  const handlePhoneSubmit = (event: FormEvent) => {
    event.preventDefault();
    const kind = loginPhoneKind(phone);
    if (kind === 'incomplete') {
      setPhoneError('Enter a valid phone number.');
      setPhoneNotice(null);
      return;
    }
    if (kind === 'other') {
      const notice = isUnitedStatesPhone(phone)
        ? "We can't text American phone numbers. Enter your email and we'll send the login code there."
        : "We can't text this number. Enter your email and we'll send the login code there.";
      setPhoneError(null);
      setPhoneNotice(notice);
      if (!phoneNotice || !emailLooksValid(email)) {
        setEmailError(phoneNotice ? 'Enter a valid email address.' : null);
        return;
      }
      startEmailLogin(email);
      return;
    }
    setPhoneNotice(null);
    startSmsLogin();
  };

  const handleEmailSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!emailLooksValid(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    startEmailLogin(email);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4 sm:p-6 md:p-10 lg:p-12">
      <TenantAppearanceDock />
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl bg-white rounded-2xl border border-gray-200/80 shadow-lg shadow-gray-200/50 p-6 sm:p-8 md:p-10 lg:p-12 space-y-8 md:space-y-10 dark:bg-gray-800 dark:border-white/10 dark:shadow-none">
        <div className="text-center">
          <img
            src={logoFull}
            alt="Amber Peak"
            className="h-10 sm:h-11 md:h-14 w-auto mx-auto mb-6 md:mb-8"
          />
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-[2rem] font-bold text-gray-900 dark:text-white leading-snug md:leading-tight tracking-tight">
            {inviteLookupDone && inviterName ? (
              <>
                <span className="block">Hey there,</span>
                <span className="mt-2 md:mt-3 block">{inviterName} has invited you to their group!</span>
              </>
            ) : inviteLookupDone ? (
              isProspect ? 'Ready to start your housing search journey?' : 'Welcome'
            ) : (
              '\u00a0'
            )}
          </h1>
          <div className="mt-4 md:mt-6 space-y-3 text-sm md:text-base lg:text-lg leading-relaxed text-gray-500 dark:text-gray-400 max-w-prose mx-auto">
            {!inviteLookupDone ? (
              <p>{'\u00a0'}</p>
            ) : inviterName ? (
              <p>Please log in to complete your information for your group’s housing search.</p>
            ) : isProspect ? (
              <p>
                Log in or create an account with your phone number so you can
                provide us your basic information and get your housing search started.
              </p>
            ) : (
              <p>Sign in with your phone number</p>
            )}
          </div>
        </div>

        <div className="space-y-3 md:space-y-4">
          {phoneStep ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-3 md:space-y-4">
              <label htmlFor="login-phone" className="block text-sm md:text-base text-center font-semibold text-gray-900 dark:text-white">
                Phone number
              </label>
              <input
                id="login-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                autoFocus
                value={phone}
                onChange={(event) => {
                  setPhone(formatPhoneInputLive(event.target.value));
                  if (phoneError) setPhoneError(null);
                  if (phoneNotice) {
                    setPhoneNotice(null);
                    setEmailError(null);
                  }
                }}
                placeholder="(888) 888-8888"
                aria-invalid={phoneError ? true : undefined}
                className={fieldClass}
              />
              {phoneError ? (
                <p className="text-sm text-center text-red-700 dark:text-red-400" role="alert">
                  {phoneError}
                </p>
              ) : null}
              {phoneNotice ? (
                <div className="space-y-3 md:space-y-4">
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-center text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100" role="alert">
                    {phoneNotice}
                  </p>
                  <label htmlFor="login-email" className="block text-sm md:text-base text-center font-semibold text-gray-900 dark:text-white">
                    Email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (emailError) setEmailError(null);
                    }}
                    placeholder="name@email.com"
                    aria-invalid={emailError ? true : undefined}
                    className={fieldClass}
                  />
                  {emailError ? (
                    <p className="text-sm text-center text-red-700 dark:text-red-400" role="alert">
                      {emailError}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <button
                type="submit"
                className="w-full rounded-xl bg-red-700 px-5 py-4 text-base font-semibold text-white hover:bg-red-800"
              >
                Continue
              </button>
            </form>
          ) : emailStep ? (
            <form onSubmit={handleEmailSubmit} className="space-y-3 md:space-y-4">
              <label htmlFor="login-email-only" className="block text-sm md:text-base text-center font-semibold text-gray-900 dark:text-white">
                Email
              </label>
              <input
                id="login-email-only"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError(null);
                }}
                placeholder="name@email.com"
                aria-invalid={emailError ? true : undefined}
                className={fieldClass}
              />
              <p className="text-xs md:text-sm text-center text-gray-500 dark:text-gray-400">
                We&apos;ll email you a login code.
              </p>
              {emailError ? (
                <p className="text-sm text-center text-red-700 dark:text-red-400" role="alert">
                  {emailError}
                </p>
              ) : null}
              <button
                type="submit"
                className="w-full rounded-xl bg-red-700 px-5 py-4 text-base font-semibold text-white hover:bg-red-800"
              >
                Continue
              </button>
            </form>
          ) : (
            <>
              {!internationalStep && !emailStep ? (
                <button
                  type="button"
                  onClick={openPhoneStep}
                  className="w-full flex items-center gap-4 md:gap-5 px-5 md:px-6 py-4 md:py-5 bg-gray-50/80 rounded-xl border border-gray-200 shadow-sm hover:border-red-300 hover:bg-white hover:shadow-md transition-all group dark:bg-white/5 dark:border-white/10 dark:hover:border-red-500/50 dark:hover:bg-white/10"
                >
                  <div className="w-11 h-11 md:w-14 md:h-14 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center shrink-0 group-hover:bg-red-100 transition-colors dark:bg-red-900/30 dark:group-hover:bg-red-900/50">
                    <Smartphone className="w-5 h-5 md:w-6 md:h-6 text-red-700 dark:text-red-400" />
                  </div>
                  <div className="text-left min-w-0">
                    <span className="block text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
                      Continue with Phone
                    </span>
                    <span className="block text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 md:mt-1">
                      We&apos;ll text you a verification code
                    </span>
                  </div>
                </button>
              ) : null}
              <LoginInternationalPath
                step={internationalStep}
                onOpen={() => setInternationalStep('canadian_phone')}
                onUsePhoneLogin={openPhoneStep}
                onUseEmailLogin={openEmailStep}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
